import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { ArrowLeft, Plus, Pencil, Trash2, Image as ImageIcon, Loader2, Package, CheckCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const APPLICABLE_OPTIONS = [
  { value: 'gite-seul', label: 'Gite seul' },
  { value: 'gite-salle', label: 'Gite + Salle' },
  { value: 'seminaire_hebergement', label: 'Seminaire hebergement' },
  { value: 'anniversaire_gite', label: 'Anniversaire + Gite' },
];

export default function AdminExtras() {
  const navigate = useNavigate();
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(null); // null or extra object
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', price: '', image_url: '', applicable_to: [] });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [mediaDialog, setMediaDialog] = useState(false);
  const [mediaList, setMediaList] = useState([]);
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState('');

  const token = localStorage.getItem('admin_token');
  const api = useCallback((method, url, data) =>
    axios({ method, url: `${API}${url}`, data, headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchExtras = useCallback(async () => {
    try {
      const res = await api('get', '/api/admin/extras');
      setExtras(res.data);
    } catch { setError('Erreur chargement'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    fetchExtras();
  }, [token, navigate, fetchExtras]);

  const openNew = () => {
    setForm({ title: '', description: '', price: '', image_url: '', applicable_to: ['gite-seul', 'gite-salle', 'seminaire_hebergement', 'anniversaire_gite'] });
    setIsNew(true);
    setEditDialog({});
  };

  const openEdit = (extra) => {
    setForm({
      title: extra.title,
      description: extra.description || '',
      price: String(extra.price),
      image_url: extra.image_url || '',
      applicable_to: extra.applicable_to || [],
    });
    setIsNew(false);
    setEditDialog(extra);
  };

  const toggleApplicable = (val) => {
    setForm(f => ({
      ...f,
      applicable_to: f.applicable_to.includes(val)
        ? f.applicable_to.filter(v => v !== val)
        : [...f.applicable_to, val],
    }));
  };

  const handleSave = async () => {
    if (!form.title || !form.price) return;
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (isNew) {
        await api('post', '/api/admin/extras', payload);
        setSuccessMsg('Extra cree');
      } else {
        await api('put', `/api/admin/extras/${editDialog.id}`, payload);
        setSuccessMsg('Extra mis a jour');
      }
      setEditDialog(null);
      fetchExtras();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api('delete', `/api/admin/extras/${deleteConfirm.id}`);
      setSuccessMsg('Extra supprime');
      setDeleteConfirm(null);
      fetchExtras();
    } catch { setError('Erreur suppression'); }
  };

  const openMediaPicker = async () => {
    setMediaDialog(true);
    try {
      const res = await api('get', '/api/media');
      setMediaList(res.data);
    } catch { setMediaList([]); }
  };

  const selectMedia = (url) => {
    setForm(f => ({ ...f, image_url: url }));
    setMediaDialog(false);
  };

  useEffect(() => { if (successMsg) { const t = setTimeout(() => setSuccessMsg(''), 3000); return () => clearTimeout(t); } }, [successMsg]);
  useEffect(() => { if (error) { const t = setTimeout(() => setError(''), 5000); return () => clearTimeout(t); } }, [error]);

  if (loading) return <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#2C3E30]" /></div>;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="bg-[#2C3E30] text-[#F2F0E9] px-6 md:px-12 py-4">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-[#F2F0E9]/70 hover:text-[#F2F0E9]" data-testid="extras-back-btn">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-[#C4A962]" />
              <h1 className="font-['Playfair_Display'] text-lg md:text-xl">Extras personnalisables</h1>
            </div>
          </div>
          <Button data-testid="add-extra-btn" size="sm" onClick={openNew} className="bg-[#C4A962] text-[#2C3E30] hover:bg-[#b5945a]">
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-8">
        {successMsg && <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm flex items-center gap-2" data-testid="extras-success"><CheckCircle className="w-4 h-4" /> {successMsg}</div>}
        {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2" data-testid="extras-error"><AlertTriangle className="w-4 h-4" /> {error}</div>}

        {extras.length === 0 ? (
          <div className="text-center py-16 text-[#595959]">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucun extra configure</p>
            <Button onClick={openNew} variant="outline" className="mt-4">Ajouter un extra</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {extras.map(ex => (
              <div key={ex.id} className={`bg-white rounded-xl shadow-sm overflow-hidden border transition-all hover:shadow-md ${!ex.active ? 'opacity-60' : ''}`} data-testid={`extra-card-${ex.id}`}>
                {ex.image_url ? (
                  <div className="h-40 bg-[#F5F5F0] overflow-hidden">
                    <img src={ex.image_url.startsWith('http') ? ex.image_url : `${API}${ex.image_url}`} alt={ex.title} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="h-40 bg-[#F5F5F0] flex items-center justify-center">
                    <ImageIcon className="w-10 h-10 text-[#ccc]" />
                  </div>
                )}
                <div className="p-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-[#2C3E30]">{ex.title}</h3>
                      <p className="text-xl font-bold text-[#C4A962]">{ex.price} EUR</p>
                    </div>
                    {!ex.active && <Badge className="bg-gray-100 text-gray-600 border border-gray-200 text-[10px]">Inactif</Badge>}
                  </div>
                  {ex.description && <p className="text-xs text-[#595959] line-clamp-2">{ex.description}</p>}
                  <div className="flex flex-wrap gap-1">
                    {(ex.applicable_to || []).map(a => (
                      <Badge key={a} className="text-[9px] bg-[#F5F5F0] text-[#595959] border-[#E5E5E0] border">
                        {APPLICABLE_OPTIONS.find(o => o.value === a)?.label || a}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-[#F5F5F0]">
                    <Button data-testid={`edit-extra-${ex.id}`} size="sm" variant="ghost" onClick={() => openEdit(ex)} className="text-[#595959] hover:text-[#2C3E30] text-xs flex-1">
                      <Pencil className="w-3 h-3 mr-1" /> Modifier
                    </Button>
                    <Button data-testid={`delete-extra-${ex.id}`} size="sm" variant="ghost" onClick={() => setDeleteConfirm(ex)} className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(o) => { if (!o) setEditDialog(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2C3E30]">{isNew ? 'Ajouter un extra' : 'Modifier l\'extra'}</DialogTitle>
            <DialogDescription>{isNew ? 'Creez un nouvel extra personnalisable.' : `Modification de "${editDialog?.title || ''}"`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1">Titre</label>
              <Input data-testid="extra-title-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: Frigo plein" className="border-[#E5E5E0]" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1">Description</label>
              <textarea data-testid="extra-desc-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="Description de l'extra..." className="w-full rounded-md border border-[#E5E5E0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8A9A5B]/30" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#2C3E30] block mb-1">Prix (EUR)</label>
                <Input data-testid="extra-price-input" type="number" min="0" step="1" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} className="border-[#E5E5E0]" />
                <p className="text-[9px] text-[#595959] mt-0.5">Forfait unique par sejour</p>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2C3E30] block mb-1">Image</label>
                {form.image_url ? (
                  <div className="relative group">
                    <img src={form.image_url.startsWith('http') ? form.image_url : `${API}${form.image_url}`} alt="" className="w-full h-20 object-cover rounded-lg border" />
                    <div className="absolute inset-0 bg-black/40 rounded-lg opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2 transition-opacity">
                      <Button size="sm" variant="ghost" className="text-white text-xs" onClick={openMediaPicker}>Changer</Button>
                      <Button size="sm" variant="ghost" className="text-white text-xs" onClick={() => setForm(f => ({ ...f, image_url: '' }))}>Retirer</Button>
                    </div>
                  </div>
                ) : (
                  <Button data-testid="extra-image-btn" variant="outline" size="sm" onClick={openMediaPicker} className="w-full border-dashed border-[#E5E5E0] text-[#595959] hover:bg-[#F5F5F0]">
                    <ImageIcon className="w-4 h-4 mr-1" /> Choisir une image
                  </Button>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1">Applicable aux formules</label>
              <div className="flex flex-wrap gap-2">
                {APPLICABLE_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    data-testid={`applicable-${opt.value}`}
                    onClick={() => toggleApplicable(opt.value)}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${form.applicable_to.includes(opt.value) ? 'bg-[#8A9A5B] text-white border-[#8A9A5B]' : 'bg-white text-[#595959] border-[#E5E5E0] hover:bg-[#F5F5F0]'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog(null)}>Annuler</Button>
            <Button data-testid="save-extra-btn" disabled={saving || !form.title || !form.price} onClick={handleSave} className="bg-[#8A9A5B] text-white hover:bg-[#7a8a4d]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {isNew ? 'Creer' : 'Sauvegarder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(o) => { if (!o) setDeleteConfirm(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#2C3E30]">Supprimer l'extra</DialogTitle>
            <DialogDescription>Supprimer "{deleteConfirm?.title}" ? Cette action est irreversible.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>Annuler</Button>
            <Button data-testid="confirm-delete-extra-btn" onClick={handleDelete} className="bg-red-600 text-white hover:bg-red-700">Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Picker Dialog */}
      <Dialog open={mediaDialog} onOpenChange={setMediaDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2C3E30]">Choisir une image</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-2">Ou entrer une URL directement :</label>
              <div className="flex gap-2">
                <Input data-testid="extra-image-url-input" placeholder="https://..." value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} className="border-[#E5E5E0] flex-1" />
                <Button size="sm" onClick={() => setMediaDialog(false)} className="bg-[#8A9A5B] text-white hover:bg-[#7a8a4d]">OK</Button>
              </div>
            </div>
            {mediaList.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-[#2C3E30] mb-2">Medias disponibles :</p>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {mediaList.filter(m => m.type === 'image').map(m => (
                    <button key={m.id} onClick={() => selectMedia(m.thumbnail_url || m.url)} className="rounded-lg overflow-hidden border border-[#E5E5E0] hover:ring-2 hover:ring-[#8A9A5B] transition-all aspect-square">
                      <img src={`${API}${m.thumbnail_url || m.url}`} alt={m.title || ''} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
